import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Breadcrumbs,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import React from 'react';
import fragments from 'services/fragments';
import { Account } from 'types/Account';
import { PlaceTimeline, TimelineLog, TimelineTypeEnum } from 'types/timeline.types';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarToday } from '@mui/icons-material';
import ImagesBlock from 'components/ImagesBlock';
import TimelineIcon from 'components/TimelineIcon';
import dayjs from 'dayjs';
import EventDetailsSummary from './EventDetailsSummary';

const EVENT_DETAILS = gql`
  ${fragments.Timeline}
  query TimelineDetails(
    $isCloud: Boolean!
    $eventType: Int!
    $eventId: String!
    $occurrenceId: String
    $timezone: Int
    $email: String
    $masterCompanyId: Int
    $accountId: Int
  ) {
    TimelineDetails(
      isCloud: $isCloud
      eventType: $eventType
      eventId: $eventId
      occurrenceId: $occurrenceId
      timezone: $timezone
      email: $email
      masterCompanyId: $masterCompanyId
      accountId: $accountId
    ) {
      ...TimelineProperties
    }
  }
`;

function EventDetails({
  account,
  occurrenceId,
  eventType,
  eventId,
}: {
  account: Account;
  occurrenceId: string | null;
  eventType: number;
  eventId?: string;
}) {
  const { t } = useTranslation(['_common', 'events']);
  const type = eventType as TimelineTypeEnum;

  const { data, error, loading } = useQuery<{ TimelineDetails: PlaceTimeline }>(EVENT_DETAILS, {
    variables: {
      ...account,
      accountId: account.id,
      eventType: eventType,
      eventId,
      occurrenceId,
    },
  });

  if (error) {
    throw error;
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to={'/events'}>
          {t('_common:events')}
        </Link>
        <Typography color="text.primary">{t('events:events_details')}</Typography>
      </Breadcrumbs>
      <Grid container spacing={2} overflow={'auto'}>
        <Grid item xs={12} md={8} display="flex" flexDirection="column">
          <EventDetailsSummary event={data?.TimelineDetails} {...{ type, loading, account }} />
          {account?.hasPermissionToViewMedia &&
            data?.TimelineDetails?.images &&
            data.TimelineDetails.images.length > 0 && (
              <ImagesBlock images={data.TimelineDetails.images} />
            )}
        </Grid>
        <Grid item xs={12} md={4}>
          {!!data?.TimelineDetails?.event?.observation && (
            <EventObservations
              logs={[
                {
                  id: '1',
                  text: data?.TimelineDetails?.event?.observation,
                  date: data.TimelineDetails?.date,
                },
              ]}
            />
          )}
          {account?.hasPermissionToViewOccurrenceLogs &&
            data?.TimelineDetails?.logs &&
            data.TimelineDetails.logs.length > 0 && (
              <EventObservations logs={data.TimelineDetails?.logs} />
            )}
        </Grid>
      </Grid>
    </Box>
  );
}

function EventObservations({ logs }: { logs: TimelineLog[] }) {
  const { t } = useTranslation(['_common', 'events']);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">{t('_common:observations')}</Typography>
      <List>
        {logs.map((log, index) => (
          <>
            <ListItem key={log.id}>
              <ListItemIcon>
                <TimelineIcon type={TimelineTypeEnum.LOG} />
              </ListItemIcon>
              <ListItemText
                primary={log.text}
                secondary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarToday sx={{ width: '0.8rem', mr: 0.5 }} />
                    {dayjs(log.date).format('L LTS')}
                  </Box>
                }
              />
            </ListItem>

            {index < logs.length - 1 && <Divider variant="middle" />}
          </>
        ))}
      </List>
    </Paper>
  );
}

export default EventDetails;
